import { Link } from 'react-router-dom';
import type { DiscoveredPrototype } from '../shared/discoverPrototypes';
import { UM_REGISTRY } from '../prototypes/urbanmap/_registry';
import type { UMRegistryEntry } from '../prototypes/urbanmap/_registry';

interface UrbanMapHomeProps {
  prototypes: DiscoveredPrototype[];
}

// ─── Cards ────────────────────────────────────────────────────────────────────

function PrototypeCard({ proto }: { proto: DiscoveredPrototype }) {
  return (
    <Link
      to={`/urbanmap/prototype/${proto.slug}`}
      style={{
        display: 'block',
        padding: 16,
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        background: '#fff',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
    >
      <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600 }}>{proto.meta.title}</h3>
      <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{proto.meta.description}</p>
      {proto.meta.status && (
        <span style={{
          display: 'inline-block', marginTop: 8, fontSize: 11, padding: '2px 8px',
          borderRadius: 4, background: '#f1f5f9', color: '#64748b',
        }}>
          {proto.meta.status}
        </span>
      )}
    </Link>
  );
}

function RegistryCard({ entry }: { entry: UMRegistryEntry }) {
  const isComponent = entry.type === 'component';
  const linkTo = isComponent
    ? `/urbanmap/component/${entry.slug}`
    : `/urbanmap/page/${entry.slug}`;

  const cardStyle: React.CSSProperties = {
    padding: 16,
    borderRadius: 8,
    border: `1px solid ${isComponent ? '#dbeafe' : '#d1fae5'}`,
    background: isComponent ? '#f0f9ff' : '#ecfdf5',
    transition: 'box-shadow 0.15s',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    cursor: 'pointer',
  };

  return (
    <Link
      to={linkTo}
      style={cardStyle}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{
          fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
          padding: '2px 6px', borderRadius: 4,
          background: isComponent ? '#bfdbfe' : '#a7f3d0',
          color: isComponent ? '#1e40af' : '#065f46',
        }}>
          {isComponent ? 'Composant' : 'Page'}
        </span>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{entry.title}</h3>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>{entry.description}</p>
      <code style={{ display: 'block', marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
        {entry.path}
      </code>
    </Link>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function UrbanMapHome({ prototypes }: UrbanMapHomeProps) {
  const components = UM_REGISTRY.filter((e) => e.type === 'component');
  const pages = UM_REGISTRY.filter((e) => e.type === 'page');

  return (
    <div style={{ padding: 40, maxWidth: 1000, margin: '0 auto' }}>
      <Link
        to="/"
        title="Retour à l'accueil Prototyper Studio (choix Size2Mix / Urban Map)"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          marginBottom: 16, padding: '4px 8px', borderRadius: 6,
          color: '#64748b', textDecoration: 'none', fontSize: 12, fontWeight: 500,
          background: '#f1f5f9', border: '1px solid #e2e8f0',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#e2e8f0'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#f1f5f9'; }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>home</span>
        Accueil
      </Link>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Urban Map — Studio</h1>
      <p style={{ color: '#64748b', marginBottom: 32 }}>
        Prototypes et composants réutilisables pour l'application UrbanMap.
      </p>

      {/* ─── Composants & Pages de base ─── */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: '#334155' }}>
          Composants &amp; Pages de base
        </h2>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
          Briques réutilisables pour construire des prototypes. Importez-les depuis <code>../_components</code> ou <code>../_pages</code>.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {pages.map((entry) => <RegistryCard key={entry.slug} entry={entry} />)}
          {components.map((entry) => <RegistryCard key={entry.slug} entry={entry} />)}
        </div>
      </section>

      {/* ─── Prototypes ─── */}
      <section>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: '#334155' }}>
          Prototypes
        </h2>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
          Maquettes interactives cliquables.
        </p>
        {prototypes.length === 0 ? (
          <p style={{ color: '#94a3b8' }}>Aucun prototype pour le moment.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {prototypes.map((proto) => <PrototypeCard key={proto.slug} proto={proto} />)}
          </div>
        )}
      </section>
    </div>
  );
}
